/**
 * Indicator utilities for LastVisiblePriceIndicator and related components.
 * Pure functions for calculating indicator data and building arrow paths.
 * No React dependencies.
 */

import { getContrastColor } from "./colorPalette";
import { formatPrice } from "./formatters";
import { getYValue } from "./pathGenerators";
import { calculateDirectionColor } from "./priceSeriesUtils";
import type {
  ChartLayout,
  ChartStoreState,
  ChartTypography,
  OhlcvPoint,
  PricePoint,
  Scale,
} from "~types/types";

// ==================== Types ====================

export interface IndicatorData {
  /** X position of the dot at the last visible point */
  dotX: number;
  /** Y position of the indicator */
  y: number;
  /** Formatted price label */
  priceLabel: string;
  /** Indicator color (direction-based or palette) */
  color: string;
  /** Text color for label (contrast with background) */
  textColor: string;
  /** Whether Y-axis is on the right side */
  yAxisOnRight: boolean;
  /** Line start X position */
  lineX1: number;
  /** Line end X position */
  lineX2: number;
  /** Typography settings for label sizing */
  typography: ChartTypography;
}

export interface CalculateIndicatorDataOptions {
  /** Series ID to calculate indicator for */
  seriesId: string;
  /** Locale for price formatting */
  locale: string;
  /** Chart store state */
  state: ChartStoreState;
  /** X and Y scales */
  scales: { x: Scale; y: Scale };
  /** Chart layout */
  layout: ChartLayout;
  /** Visible data within viewport */
  visibleData: (PricePoint | OhlcvPoint)[];
}

// ==================== Indicator Data Calculation ====================

/**
 * Calculate indicator data for the last visible price point.
 * Returns null if no valid data to display.
 */
export function calculateIndicatorData(
  options: CalculateIndicatorDataOptions
): IndicatorData | null {
  const { seriesId, locale, state, scales, layout, visibleData } = options;

  if (visibleData.length === 0) {
    return null;
  }

  // getVisibleData now returns only truly visible data (no padding),
  // so no additional filtering is needed

  // Get the last visible point
  const lastPoint = visibleData.at(-1);

  if (!lastPoint) {
    return null;
  }

  const lastValue = getYValue(lastPoint);
  const dotX = scales.x.scale(lastPoint.timestamp);
  const y = scales.y.scale(lastValue);

  // Check if the price is within visible y range
  const [yMin, yMax] = scales.y.domain();

  if (lastValue < yMin || lastValue > yMax) {
    return null;
  }

  // Get series metadata
  const seriesMeta = state.seriesMeta.get(seriesId);

  // Determine color using shared utility
  const color = calculateDirectionColor({
    mode: state.mode,
    visibleData,
    seriesMeta,
    providedColor: seriesMeta?.color,
  });

  // Get currency from series metadata
  const currency = seriesMeta?.issuedCurrency ?? "USD";

  // Use compact notation consistent with y-axis labels
  const useCompact = Math.abs(yMax) >= 10000 || Math.abs(yMin) >= 10000;

  // Format the price
  const priceLabel = formatPrice(lastValue, {
    currency,
    locale,
    compact: useCompact,
  });

  // Determine Y-axis position (right or left)
  const yAxisOnRight = layout.yAxisArea.x > layout.priceArea.x;

  // Get contrast color for text on colored background
  const textColor = getContrastColor(color);

  return {
    dotX,
    y,
    priceLabel,
    color,
    textColor,
    yAxisOnRight,
    // Line spans full chart width like crosshair horizontal
    lineX1: layout.priceArea.x,
    lineX2: layout.priceArea.x + layout.priceArea.width,
    typography: layout.typography,
  };
}

// ==================== Arrow Path Building ====================

export interface ArrowPathOptions {
  /** Label Y position (top of label) */
  labelY: number;
  /** Label height */
  labelHeight: number;
  /** Center Y position (where arrow points) */
  centerY: number;
  /** Total label width (including arrow) */
  totalLabelWidth: number;
  /** Arrow width */
  arrowWidth: number;
  /** Label X position */
  labelX: number;
  /** Text rectangle width (excluding arrow) */
  textWidth: number;
  /** Whether Y-axis is on the right side */
  yAxisOnRight: boolean;
}

/**
 * Build arrow-shaped path for indicator label background.
 * Arrow points toward the chart (left when Y-axis is right, right when Y-axis is left).
 */
export function buildArrowPath(options: ArrowPathOptions): string {
  const {
    labelY,
    labelHeight,
    centerY,
    totalLabelWidth,
    arrowWidth,
    labelX,
    textWidth,
    yAxisOnRight,
  } = options;

  const top = labelY;
  const bottom = labelY + labelHeight;

  if (yAxisOnRight) {
    // Y-axis on right: arrow points left (toward chart)
    // Shape: arrow tip on left, rectangle on right
    const arrowTip = labelX;
    const rectLeft = labelX + arrowWidth;
    const rectRight = labelX + totalLabelWidth;

    return `
      M ${arrowTip} ${centerY}
      L ${rectLeft} ${top}
      L ${rectRight} ${top}
      L ${rectRight} ${bottom}
      L ${rectLeft} ${bottom}
      Z
    `;
  }

  // Y-axis on left: arrow points right (toward chart)
  // Shape: rectangle on left, arrow tip on right
  const rectLeft = labelX;
  const rectRight = labelX + textWidth;
  const arrowTip = labelX + totalLabelWidth;

  return `
    M ${rectLeft} ${top}
    L ${rectRight} ${top}
    L ${arrowTip} ${centerY}
    L ${rectRight} ${bottom}
    L ${rectLeft} ${bottom}
    Z
  `;
}

// ==================== Label Positioning ====================

export interface LabelPositionOptions {
  /** Y position of the line */
  y: number;
  /** Line end X position (typically right edge of chart) */
  lineX2: number;
  /** Line start X position (typically left edge of chart) */
  lineX1: number;
  /** Whether Y-axis is on the right side */
  yAxisOnRight: boolean;
  /** Price label text */
  priceLabel: string;
  /** Typography settings */
  typography: ChartTypography;
}

export interface LabelDimensions {
  /** Padding X */
  paddingX: number;
  /** Padding Y */
  paddingY: number;
  /** Arrow width */
  arrowWidth: number;
  /** Gap between line and label */
  labelGap: number;
  /** Text width */
  textWidth: number;
  /** Label height */
  labelHeight: number;
  /** Total label width including arrow */
  totalLabelWidth: number;
  /** Label X position */
  labelX: number;
  /** Label Y position */
  labelY: number;
  /** Text X position (centered in rectangle portion) */
  textX: number;
}

/**
 * Calculate label positioning and dimensions for the indicator.
 */
export function calculateLabelDimensions(
  options: LabelPositionOptions
): LabelDimensions {
  const { y, lineX2, lineX1, yAxisOnRight, priceLabel, typography } = options;

  // Label positioning constants
  const paddingX = 6;
  const paddingY = 3;
  const arrowWidth = 6;
  const labelGap = 2;
  const { axisLabelSize, charWidth } = typography;

  // Calculate label dimensions (excluding arrow)
  const textWidth = priceLabel.length * charWidth + paddingX * 2;
  const labelHeight = axisLabelSize + paddingY * 2;

  // Total label width includes the arrow
  const totalLabelWidth = textWidth + arrowWidth;

  // Label positioning based on Y-axis position
  // Arrow points toward the chart
  const labelX = yAxisOnRight
    ? lineX2 + labelGap
    : lineX1 - totalLabelWidth - labelGap;

  // Label Y position: centered on the line
  const labelY = y - labelHeight / 2;

  // Text X position: centered in the rectangle portion (excluding arrow)
  const textX = yAxisOnRight
    ? labelX + arrowWidth + textWidth / 2
    : labelX + textWidth / 2;

  return {
    paddingX,
    paddingY,
    arrowWidth,
    labelGap,
    textWidth,
    labelHeight,
    totalLabelWidth,
    labelX,
    labelY,
    textX,
  };
}
